export const IMPERSONATION_STORAGE_KEY = 'krece_impersonation';
export const IMPERSONATION_LOGIN_MESSAGE_KEY = 'krece_impersonation_login_message';
export const IMPERSONATION_DURATION_MS = 15 * 60 * 1000;
export const IMPERSONATION_EXPIRED_MESSAGE = 'Tu sesión de Logear como expiró. Por favor inicia sesión nuevamente.';

export type ImpersonationSession = {
	originalEmail: string;
	originalToken: string;
	targetEmail: string;
	startedAt: number;
	expiresAt: number;
};

export function getImpersonationSession(): ImpersonationSession | null {
	try {
		const rawSession = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
		if (!rawSession) return null;

		const session = JSON.parse(rawSession) as Partial<ImpersonationSession>;
		if (!session.originalEmail || !session.originalToken || !session.targetEmail || !session.expiresAt) {
			clearImpersonationSession();
			return null;
		}

		return session as ImpersonationSession;
	} catch (error) {
		clearImpersonationSession();
		return null;
	}
}

export function saveImpersonationSession(session: ImpersonationSession) {
	localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
}

export function clearImpersonationSession() {
	try {
		localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
	} catch {
		// Storage denial must not block logout or a normal seller login.
	}
}

export function isImpersonationExpired(session: ImpersonationSession) {
	return Date.now() >= session.expiresAt;
}

export function getActiveImpersonationSession(currentEmail?: string | null) {
	const session = getImpersonationSession();
	if (!session) return null;

	if (isImpersonationExpired(session)) {
		clearImpersonationSession();
		return null;
	}

	if (currentEmail && session.targetEmail.toLowerCase() !== currentEmail.toLowerCase()) {
		clearImpersonationSession();
		return null;
	}

	return session;
}

export function getImpersonationHeaders(session?: ImpersonationSession | null): Record<string, string> {
	const activeSession = session ?? getActiveImpersonationSession();
	if (!activeSession) return {};

	return {
		'X-Impersonated-By': activeSession.originalEmail,
		'X-Impersonated-Store': activeSession.targetEmail,
		'X-Impersonation-Expires-At': new Date(activeSession.expiresAt).toISOString()
	};
}

export function saveImpersonationExpiredMessage() {
	try {
		localStorage.setItem(IMPERSONATION_LOGIN_MESSAGE_KEY, IMPERSONATION_EXPIRED_MESSAGE);
	} catch {
		// The notice is optional; expiring the session is not.
	}
}

export function consumeImpersonationLoginMessage() {
	try {
		const message = localStorage.getItem(IMPERSONATION_LOGIN_MESSAGE_KEY);
		if (message) localStorage.removeItem(IMPERSONATION_LOGIN_MESSAGE_KEY);
		return message;
	} catch {
		return null;
	}
}
